import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { User } from '../types';
import { CATEGORIES } from '../constants';
import { updateUserProfile, checkEmailExists } from '../services/dataService';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (user: User) => void;
  requireProfileCompletion?: boolean;
  userToComplete?: User | null;
}

type AuthStep = 'LOGIN' | 'SIGNUP' | 'COMPLETE_PROFILE' | 'FORGOT_PASSWORD';

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  onLoginSuccess,
  requireProfileCompletion = false,
  userToComplete = null
}) => {
  const [step, setStep] = useState<AuthStep>('LOGIN');

  // Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [targetExam, setTargetExam] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // Temp user state for multi-step flows (like Google Auth completion)
  const [partialUser, setPartialUser] = useState<User | null>(null);

  // Handle external triggers for profile completion
  useEffect(() => {
    if (isOpen) {
      if (requireProfileCompletion && userToComplete) {
        setStep('COMPLETE_PROFILE');
        setPartialUser(userToComplete);
        setFullName(userToComplete.name || '');
      } else if (step === 'COMPLETE_PROFILE' && !requireProfileCompletion) {
        setStep('LOGIN');
      }
    }
  }, [isOpen, requireProfileCompletion, userToComplete]);

  if (!isOpen) return null;

  const handleGoogleLogin = async () => {
    try {
      if (supabase) {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: window.location.origin,
          },
        });
        if (error) throw error;
        // OAuth redirects, logic continues in App.tsx session listener
      } else {
        // Mock Google Login
        setLoading(true);
        await new Promise(resolve => setTimeout(resolve, 1000));

        const mockGoogleUser: User = {
          id: 'mock-google-' + Date.now(),
          email: 'google.user@example.com',
          name: 'Google User',
          role: 'user',
          subscriptionTier: 'free'
          // Missing phone & targetExam
        };

        setPartialUser(mockGoogleUser);
        setFullName(mockGoogleUser.name || '');
        setStep('COMPLETE_PROFILE');
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.message || 'Google Login failed');
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResetSuccess(false);

    try {
      if (!email) throw new Error('Please enter your email address');

      if (supabase) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setResetSuccess(true);
      } else {
        // Mock mode
        await new Promise(resolve => setTimeout(resolve, 1000));
        setResetSuccess(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleStandardAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (supabase) {
        if (step === 'LOGIN') {
          // --- LOGIN FLOW ---
          const { data, error } = await supabase.auth.signInWithPassword({ email, password });
          if (error) throw error;

          if (data.user) {
            // Check if profile is complete
            const { data: profile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', data.user.id)
              .single();

            const user: User = {
              id: data.user.id,
              email: data.user.email!,
              role: (profile?.role as any) || 'user',
              name: profile?.name,
              phone: profile?.phone,
              targetExam: profile?.target_exam,
              subscriptionTier: profile?.subscription_tier || 'free'
            };

            // If profile incomplete, move to COMPLETE_PROFILE step
            if (!user.phone || !user.targetExam) {
              setPartialUser(user);
              if (user.name) setFullName(user.name);
              setStep('COMPLETE_PROFILE');
            } else {
              onLoginSuccess(user);
              onClose();
            }
          }
        } else {
          // --- SIGNUP FLOW ---
          if (!fullName || !phone || !targetExam) {
            throw new Error("Please fill all fields");
          }

          // Check if email already exists
          const emailExists = await checkEmailExists(email);
          if (emailExists) {
            throw new Error("An account already exists with this email. Please login instead.");
          }

          const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                full_name: fullName,
                phone: phone,
                target_exam: targetExam
              }
            }
          });

          if (error) throw error;

          if (data.user) {
            const matchedCategory = CATEGORIES.find(
              c => c.name.toLowerCase() === targetExam.toLowerCase() || c.id.toLowerCase() === targetExam.toLowerCase()
            );
            const initialAllowedCategories = matchedCategory ? [matchedCategory.id] : [];

            // Manually save profile details to 'profiles' table if triggers aren't set up
            await updateUserProfile(data.user.id, {
              name: fullName,
              phone: phone,
              targetExam: targetExam,
              email: email,
              allowedCategories: initialAllowedCategories
            });

            const user: User = {
              id: data.user.id,
              email: data.user.email!,
              role: 'user',
              name: fullName,
              phone: phone,
              targetExam: targetExam,
              subscriptionTier: 'free',
              allowedCategories: initialAllowedCategories
            };
            onLoginSuccess(user);
            onClose();
          }
        }
      } else {
        // --- MOCK MODE ---
        await new Promise(resolve => setTimeout(resolve, 800));

        if (step === 'SIGNUP' && (!fullName || !phone || !targetExam)) {
          throw new Error("Please fill all fields");
        }

        const matchedCategory = CATEGORIES.find(
          c => c.name.toLowerCase() === targetExam.toLowerCase() || c.id.toLowerCase() === targetExam.toLowerCase()
        );
        const initialAllowedCategories = matchedCategory ? [matchedCategory.id] : [];

        const user: User = {
          id: 'mock-user-' + Date.now(),
          email: email,
          role: email.includes('admin') ? 'admin' : 'user',
          name: fullName || email.split('@')[0],
          phone: phone,
          targetExam: targetExam,
          subscriptionTier: email.includes('admin') ? 'premium' : 'free',
          allowedCategories: email.includes('admin') ? [] : initialAllowedCategories
        };

        // Save mock user categories in the mock user table
        const stored = localStorage.getItem('thrusted_admin_mock_users');
        if (stored) {
          const users = JSON.parse(stored) as User[];
          const updated = [...users, user];
          localStorage.setItem('thrusted_admin_mock_users', JSON.stringify(updated));
        }

        onLoginSuccess(user);
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileCompletion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!partialUser) return;
    setLoading(true);

    try {
      if (!phone || !targetExam) throw new Error("Please complete your profile");

      const matchedCategory = CATEGORIES.find(
        c => c.name.toLowerCase() === targetExam.toLowerCase() || c.id.toLowerCase() === targetExam.toLowerCase()
      );
      const initialAllowedCategories = matchedCategory ? [matchedCategory.id] : [];

      // Save to DB and Update Cache
      // updateUserProfile handles both Supabase and LocalStorage updates now
      await updateUserProfile(partialUser.id, {
        name: fullName || partialUser.name || '',
        phone,
        targetExam,
        email: partialUser.email,
        allowedCategories: initialAllowedCategories
      });

      const updatedUser: User = {
        ...partialUser,
        name: fullName || partialUser.name,
        phone,
        targetExam,
        allowedCategories: initialAllowedCategories
      };

      onLoginSuccess(updatedUser);
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md">
      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-glass-lg w-full max-w-md overflow-hidden relative animate-scale-in">
        {/* Gradient Header */}
        <div className="h-2 bg-gradient-to-r from-orange-500 via-red-500 to-purple-500"></div>

        {step !== 'COMPLETE_PROFILE' && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 flex items-center justify-center transition-colors z-10"
          >
            <i className="fas fa-times"></i>
          </button>
        )}

        <div className="p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-2xl flex items-center justify-center text-3xl mx-auto mb-5 shadow-lg shadow-orange-500/30">
              {step === 'LOGIN' && <i className="fas fa-sign-in-alt"></i>}
              {step === 'SIGNUP' && <i className="fas fa-user-plus"></i>}
              {step === 'COMPLETE_PROFILE' && <i className="fas fa-user-check"></i>}
              {step === 'FORGOT_PASSWORD' && <i className="fas fa-key"></i>}
            </div>
            <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
              {step === 'LOGIN' && 'Welcome Back'}
              {step === 'SIGNUP' && 'Create Account'}
              {step === 'COMPLETE_PROFILE' && 'Complete Profile'}
              {step === 'FORGOT_PASSWORD' && 'Reset Password'}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-2 text-sm">
              {step === 'LOGIN' && 'Sign in to continue your prep journey'}
              {step === 'SIGNUP' && 'Join 10,000+ aspirants today'}
              {step === 'COMPLETE_PROFILE' && 'Just a few more details to personalize your experience'}
              {step === 'FORGOT_PASSWORD' && "We'll send you a reset link"}
            </p>
          </div>

          {step !== 'COMPLETE_PROFILE' && step !== 'FORGOT_PASSWORD' && (
            <>
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-white dark:bg-slate-700 border-2 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 text-slate-700 dark:text-white font-semibold py-3.5 rounded-xl transition-all hover:shadow-md group"
              >
                <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5 group-hover:scale-110 transition-transform" alt="Google" />
                Continue with Google
              </button>

              <div className="relative flex py-4 items-center my-6">
                <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
                <span className="flex-shrink-0 mx-4 text-slate-400 dark:text-slate-500 text-xs font-medium uppercase tracking-wider">Or with email</span>
                <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
              </div>
            </>
          )}

          <form onSubmit={step === 'COMPLETE_PROFILE' ? handleProfileCompletion : (step === 'FORGOT_PASSWORD' ? handleForgotPassword : handleStandardAuth)} className="space-y-4">

            {/* Fields for SIGNUP or COMPLETE_PROFILE */}
            {(step === 'SIGNUP' || step === 'COMPLETE_PROFILE') && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Full Name</label>
                  <div className="relative">
                    <i className="fas fa-user absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white transition-all"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Phone Number</label>
                  <div className="relative">
                    <i className="fas fa-phone absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white transition-all"
                      placeholder="+91 98765 43210"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Target Exam</label>
                  <div className="relative">
                    <i className="fas fa-bullseye absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                    <select
                      value={targetExam}
                      onChange={(e) => setTargetExam(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white appearance-none transition-all"
                      required
                    >
                      <option value="">Select Exam Category</option>
                      {CATEGORIES.map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                    <i className="fas fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"></i>
                  </div>
                </div>
              </>
            )}

            {/* Standard Login Fields */}
            {step !== 'COMPLETE_PROFILE' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Email Address</label>
                  <div className="relative">
                    <i className="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white transition-all"
                      placeholder="name@example.com"
                      required
                    />
                  </div>
                </div>

                {step !== 'FORGOT_PASSWORD' && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Password</label>
                      {step === 'LOGIN' && (
                        <button
                          type="button"
                          onClick={() => { setStep('FORGOT_PASSWORD'); setError(''); setResetSuccess(false); }}
                          className="text-xs text-orange-600 dark:text-orange-400 hover:underline font-medium"
                        >
                          Forgot Password?
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <i className="fas fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white transition-all"
                        placeholder="••••••••"
                        required
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm rounded-xl flex items-center border border-red-200 dark:border-red-800">
                <i className="fas fa-exclamation-circle mr-3"></i> {error}
              </div>
            )}

            {resetSuccess && (
              <div className="p-4 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-sm rounded-xl flex items-center border border-green-200 dark:border-green-800">
                <i className="fas fa-check-circle mr-3"></i> Password reset email sent! Check your inbox.
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary text-white py-4 rounded-xl font-bold text-base disabled:opacity-70 flex justify-center items-center mt-6"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  {step === 'LOGIN' && <><i className="fas fa-sign-in-alt mr-2"></i> Sign In</>}
                  {step === 'SIGNUP' && <><i className="fas fa-user-plus mr-2"></i> Create Account</>}
                  {step === 'FORGOT_PASSWORD' && <><i className="fas fa-paper-plane mr-2"></i> Send Reset Link</>}
                  {step === 'COMPLETE_PROFILE' && <><i className="fas fa-check mr-2"></i> Complete Profile</>}
                </>
              )}
            </button>
          </form>

          {step !== 'COMPLETE_PROFILE' && (
            <div className="mt-6 text-center text-sm text-slate-600 dark:text-slate-400">
              {step === 'FORGOT_PASSWORD' ? (
                <>
                  Remember your password?{' '}
                  <button
                    onClick={() => { setStep('LOGIN'); setError(''); setResetSuccess(false); }}
                    className="text-orange-600 dark:text-orange-400 font-bold hover:underline"
                  >
                    Back to Login
                  </button>
                </>
              ) : (
                <>
                  {step === 'LOGIN' ? "Don't have an account? " : "Already have an account? "}
                  <button
                    onClick={() => { setStep(step === 'LOGIN' ? 'SIGNUP' : 'LOGIN'); setError(''); }}
                    className="text-orange-600 dark:text-orange-400 font-bold hover:underline"
                  >
                    {step === 'LOGIN' ? 'Sign Up' : 'Log In'}
                  </button>
                </>
              )}
            </div>
          )}

          {!supabase && step !== 'COMPLETE_PROFILE' && (
            <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700 text-xs text-center text-slate-400 dark:text-slate-500">
              <i className="fas fa-info-circle mr-1"></i> Demo Mode: Use 'admin@thrusted.in' for Admin access.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;